import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Switch,
  Space,
  message,
  InputNumber,
  Select,
  Row,
  Col,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import { TokenService, TaskService } from "../../services";
import {
  TokenConfig,
  CreateTokenConfigDto,
  UpdateTokenConfigDto,
  TaskTypeOption,
} from "../../types/token";
import ErrorHandler from "../../utils/errorHandler";
import MainLayout from "../../components/layout/MainLayout";
import "./TokenConfig.scss";

const TokenConfigPage: React.FC = () => {
  const [tokenConfigs, setTokenConfigs] = useState<TokenConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TokenConfig | null>(null);
  const [taskTypeOptions, setTaskTypeOptions] = useState<TaskTypeOption[]>([]);
  const [configForm] = Form.useForm();

  // 加载Token配置列表
  const loadTokenConfigs = async () => {
    setLoading(true);
    try {
      const response = await TokenService.getAllConfigs();
      if (response.code === 0 && response.data) {
        setTokenConfigs(response.data);
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 加载任务类型列表
  const loadTaskTypeOptions = async () => {
    try {
      const response = await TaskService.getTaskTypeList();
      if (response.code === 0 && response.data) {
        setTaskTypeOptions(response.data.types);
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadTokenConfigs();
    loadTaskTypeOptions();
  }, []);

  // 保存Token配置
  const handleSaveConfig = async (
    values: CreateTokenConfigDto | UpdateTokenConfigDto
  ) => {
    try {
      setLoading(true);
      let response;

      if (editingConfig) {
        // 更新
        response = await TokenService.updateConfig(editingConfig.id, values);
      } else {
        // 创建
        response = await TokenService.createConfig(
          values as CreateTokenConfigDto
        );
      }

      if (response.code === 0) {
        message.success(`${editingConfig ? "更新" : "创建"}配置成功`);
        handleCloseModal();
        loadTokenConfigs();
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 打开配置弹窗
  const handleOpenModal = (config?: TokenConfig) => {
    setEditingConfig(config || null);
    // 先重置表单，确保干净的状态
    configForm.resetFields();
    setModalVisible(true);

    // 如果是编辑模式，在下一个事件循环中设置值，确保重置完成
    if (config) {
      setTimeout(() => {
        configForm.setFieldsValue(config);
      }, 0);
    }
  };

  // 关闭配置弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingConfig(null);
    // 关闭时也重置表单
    configForm.resetFields();
  };

  // 配置表格列
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "任务类型",
      dataIndex: "taskType",
      key: "taskType",
      width: 150,
    },
    {
      title: "类型名称",
      dataIndex: "taskType",
      key: "taskType",
      width: 150,
      render: (text: string) =>
        taskTypeOptions.find((option) => option.value === text)?.label,
    },
    {
      title: "Token消耗",
      dataIndex: "tokenCost",
      key: "tokenCost",
      width: 120,
      render: (cost: number) => `${cost} Token`,
    },
    {
      title: "描述",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
    },
    {
      title: "状态",
      dataIndex: "isEnabled",
      key: "isEnabled",
      width: 80,
      render: (isEnabled: boolean) => (
        <span style={{ color: isEnabled ? "#52c41a" : "#ff4d4f" }}>
          {isEnabled ? "启用" : "禁用"}
        </span>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
    },
    {
      title: "操作",
      key: "action",
      width: 150,
      render: (_: any, record: TokenConfig) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout title="Token消耗配置">
      <div className="token-config">
        {/* Token配置 */}
        <Card
          title="已配置列表"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              新增消耗配置
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={tokenConfigs}
            rowKey="id"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            }}
          />
        </Card>

        {/* 配置弹窗 */}
        <Modal
          title="Token消耗配置"
          open={modalVisible}
          onOk={() => configForm.submit()}
          onCancel={handleCloseModal}
          confirmLoading={loading}
          width={600}
        >
          <Form form={configForm} layout="vertical" onFinish={handleSaveConfig}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="taskType"
                  label="任务类型"
                  rules={[{ required: true, message: "请选择任务类型" }]}
                >
                  <Select
                    placeholder="请选择任务类型"
                    options={taskTypeOptions}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="tokenCost"
                  label="Token消耗数量"
                  rules={[
                    { required: true, message: "请输入Token消耗数量" },
                    {
                      validator: (_, value) => {
                        if (
                          value === null ||
                          value === undefined ||
                          value === ""
                        ) {
                          return Promise.reject(
                            new Error("请输入Token消耗数量")
                          );
                        }
                        const numValue = Number(value);
                        if (isNaN(numValue)) {
                          return Promise.reject(new Error("请输入有效的数字"));
                        }
                        if (numValue < 0) {
                          return Promise.reject(new Error("消耗数量不能小于0"));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="请输入消耗数量"
                    style={{ width: "100%" }}
                    min={0}
                    precision={0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="配置描述">
              <Input.TextArea placeholder="请输入配置描述" rows={3} />
            </Form.Item>

            <Form.Item
              name="isEnabled"
              label="启用状态"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default TokenConfigPage;
